type AuthConfig = {
  requestCodeUrl: string
  verifyCodeUrl: string
  nonce: string
  redirectUrl: string | null
  locale: string | null
  captcha: {
    required: boolean
    fieldName: string
  }
  fragmentKeys: {
    email: string
    code: string
  }
  messages: {
    emailLabel: string
    emailPlaceholder: string
    codeLabel: string
    continue: string
    verify: string
    back: string
    codeSent: string
    genericError: string
  }
}

type FragmentPrefill = {
  email: string
  code: string
}

export class AuthSection {
  private readonly config: AuthConfig
  private email = ''

  constructor(private readonly root: HTMLElement) {
    const configJson = root.dataset.authConfig
    if (!configJson) {
      return
    }

    this.config = JSON.parse(configJson) as AuthConfig

    const emailForm = root.querySelector<HTMLFormElement>(
      '[data-auth-step="email"]'
    )
    const codeForm = root.querySelector<HTMLFormElement>(
      '[data-auth-step="code"]'
    )
    const backButton = root.querySelector<HTMLButtonElement>('[data-auth-back]')

    this.applyFragmentPrefill(emailForm, codeForm)

    emailForm?.addEventListener('submit', (event) => {
      void this.handleEmailSubmit(event, emailForm, codeForm)
    })

    codeForm?.addEventListener('submit', (event) => {
      void this.handleCodeSubmit(event, codeForm)
    })

    backButton?.addEventListener('click', () => {
      // We reload the page to reset the form (and captcha)
      window.location.reload()
    })
  }

  /**
   * Read email and code from the URL fragment, prefill the form, then remove the fragment.
   */
  private applyFragmentPrefill(
    emailForm: HTMLFormElement | null,
    codeForm: HTMLFormElement | null
  ): void {
    const prefill = this.parseFragment()
    if (prefill === null || emailForm === null || codeForm === null) {
      return
    }

    this.email = prefill.email

    const emailInput = emailForm.querySelector<HTMLInputElement>(
      'input[name="email"]'
    )
    const codeInput = codeForm.querySelector<HTMLInputElement>(
      'input[name="code"]'
    )

    if (emailInput) {
      emailInput.value = prefill.email
    }
    if (codeInput) {
      codeInput.value = prefill.code
    }

    this.stripFragmentFromUrl()
    this.showStep(codeForm, emailForm)
    codeInput?.focus()
  }

  private parseFragment(): FragmentPrefill | null {
    const hash = window.location.hash
    if (hash.length < 2) {
      return null
    }

    const params = new URLSearchParams(hash.slice(1))
    const email = params.get(this.config.fragmentKeys.email)?.trim() ?? ''
    const code = params.get(this.config.fragmentKeys.code)?.trim() ?? ''

    if (!this.isValidEmail(email) || !/^\d{6}$/.test(code)) {
      return null
    }

    return { email, code }
  }

  private stripFragmentFromUrl(): void {
    const url = new URL(window.location.href)
    url.hash = ''
    history.replaceState(null, '', url.pathname + url.search)
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  private async handleEmailSubmit(
    event: Event,
    emailForm: HTMLFormElement,
    codeForm: HTMLFormElement | null
  ): Promise<void> {
    event.preventDefault()

    const emailInput = emailForm.querySelector<HTMLInputElement>(
      'input[name="email"]'
    )
    const email = emailInput?.value.trim() ?? ''

    if (email === '') {
      return
    }

    this.setLoading(emailForm, true)
    this.setFeedback(emailForm, '')

    try {
      const response = await this.post(this.config.requestCodeUrl, {
        email,
        redirect_url: this.config.redirectUrl,
        locale: this.config.locale,
        captcha: this.getCaptchaSolution(),
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        this.setFeedback(emailForm, data.message ?? this.config.messages.genericError)
        return
      }

      this.email = email
      this.showStep(codeForm, emailForm)
      codeForm?.querySelector<HTMLInputElement>('input[name="code"]')?.focus()
    } catch {
      this.setFeedback(emailForm, this.config.messages.genericError)
    } finally {
      this.setLoading(emailForm, false)
    }
  }

  private async handleCodeSubmit(
    event: Event,
    codeForm: HTMLFormElement
  ): Promise<void> {
    event.preventDefault()

    const codeInput = codeForm.querySelector<HTMLInputElement>(
      'input[name="code"]'
    )
    const code = codeInput?.value.trim() ?? ''

    if (!/^\d{6}$/.test(code)) {
      return
    }

    if (this.email === '') {
      const emailInput = this.root.querySelector<HTMLFormElement>(
        '[data-auth-step="email"]'
      )?.querySelector<HTMLInputElement>('input[name="email"]')
      this.email = emailInput?.value.trim() ?? ''
    }

    if (this.email === '') {
      return
    }

    this.setLoading(codeForm, true)
    this.setFeedback(codeForm, '')

    try {
      const response = await this.post(this.config.verifyCodeUrl, {
        email: this.email,
        code,
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        this.setFeedback(codeForm, data.message ?? this.config.messages.genericError)
        return
      }

      if (typeof data.redirect_url === 'string' && data.redirect_url !== '') {
        const redirectUrl = new URL(data.redirect_url, window.location.origin)
        if (redirectUrl.origin === window.location.origin) {
          window.location.href = redirectUrl.href
          return
        }
      }

      window.location.reload()
    } catch {
      this.setFeedback(codeForm, this.config.messages.genericError)
    } finally {
      this.setLoading(codeForm, false)
    }
  }

  private getCaptchaSolution(): string | null {
    if (!this.config.captcha.required) {
      return null
    }

    const field = this.root.querySelector<HTMLInputElement>(
      `[name="${this.config.captcha.fieldName}"]`
    )

    return field?.value ?? ''
  }

  private async post(
    url: string,
    body: Record<string, string | null>
  ): Promise<Response> {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': this.config.nonce,
      },
      body: JSON.stringify(body),
    })
  }

  private showStep(
    active: HTMLElement | null,
    inactive: HTMLElement | null
  ): void {
    if (active) {
      active.hidden = false
    }
    if (inactive) {
      inactive.hidden = true
    }
  }

  private setFeedback(form: HTMLFormElement | null, message: string): void {
    const feedback = form?.querySelector<HTMLElement>('[data-auth-feedback]')
    if (!feedback) {
      return
    }

    if (message === '') {
      feedback.hidden = true
      feedback.textContent = ''
      return
    }

    feedback.hidden = false
    feedback.textContent = message
  }

  private setLoading(form: HTMLFormElement, loading: boolean): void {
    const submit = form.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    )
    if (submit) {
      submit.disabled = loading
    }
  }
}
